import { gql } from "@apollo/client";

export const ME = gql`
  query Me {
    me {
      id
      fullname
      email
      phoneNumber
      additionalContact
      address
      country
      postcode
      uploadPhoto
      role
      isFirstLoggedIn
    }
  }
`;

export const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      fullname
      phoneNumber
      additionalContact
      address
      country
      postcode
      uploadPhoto
      role
      isFirstLoggedIn
    }
  }
`;

export const GET_ADMIN_LIST = gql`
  query GetAdminList($page: Int, $perPage: Int, $search: String) {
    getAdminList(page: $page, perPage: $perPage, search: $search) {
      success
      message
      data {
        id
        fullname
        email
        role
        isActive
        phoneNumber
        address
        country
        postcode
      }
      page
      perPage
      totalPages
      totalCount
    }
  }
`;

export const GET_CLASS_LIST = gql`
  query GetClassList(
    $page: Int
    $perPage: Int
    $search: String
    $instructorName: String
    $instructorId: String
    $classType: ClassType
    $day: String
    $timeline: TimelineFilter
    $today: String
    $startDate: String
    $endDate: String
    $currentTime: String
    $includeCancelled: Boolean
  ) {
    getClassList(
      page: $page
      perPage: $perPage
      search: $search
      instructorName: $instructorName
      instructorId: $instructorId
      classType: $classType
      day: $day
      timeline: $timeline
      today: $today
      startDate: $startDate
      endDate: $endDate
      currentTime: $currentTime
      includeCancelled: $includeCancelled
    ) {
      success
      message
      totalCount
      page
      perPage
      totalPages
      data {
        id
        classId
        className
        instructorName
        instructorId
        classType
        day
        scheduledDates
        totalNoOfClasses
        startTime
        endTime
        enrolledStudents
        isCancelled
        cancelledDates
        coverImage
        description
        created_at
        updated_at
      }
    }
  }
`;

// Lightweight query for the "Linked Class" dropdown in the Pass form.
// Fetches only the fields the dropdown renders, avoiding heavy fields such as
// coverImage / description / enrolledStudents that slow down GET_CLASS_LIST.
export const GET_CLASS_OPTIONS = gql`
  query GetClassOptions($page: Int, $perPage: Int, $search: String) {
    getClassList(page: $page, perPage: $perPage, search: $search) {
      success
      data {
        id
        className
        instructorName
        classType
        scheduledDates
        totalNoOfClasses
      }
    }
  }
`;

export const GET_CLASS_NAMES = gql`
  query GetClassNames($page: Int, $perPage: Int, $instructorId: String) {
    getClassList(page: $page, perPage: $perPage, instructorId: $instructorId) {
      data {
        id
        classId
        className
        instructorName
      }
    }
  }
`;

export const GET_INSTRUCTOR_UPDATES = gql`
  query GetInstructorUpdates($instructorId: String!) {
    getInstructorUpdates(instructorId: $instructorId) {
      updates {
        id
        type
        title
        message
        time
        isNew
      }
      newCount
    }
  }
`;

export const GET_INSTRUCTOR_BY_EMAIL = gql`
  query GetInstructorByEmail($search: String) {
    getInstructorList(page: 1, perPage: 1, search: $search) {
      data {
        id
        instructorId
        fullname
        email
      }
    }
  }
`;

export const GET_CLASS_BY_ID = gql`
  query GetClassById($id: ID!) {
    getClassById(id: $id) {
      success
      message
      data {
        id
        className
        instructorName
        instructorId
        classType
        day
        scheduledDates
        startTime
        endTime
        enrolledStudents
        coverImage
        description
        isCancelled
        cancelledDates
        created_at
        updated_at
      }
    }
  }
`;
export const GET_INSTRUCTOR_LIST = gql`
  query GetInstructorList($page: Int, $perPage: Int, $search: String) {
    getInstructorList(page: $page, perPage: $perPage, search: $search) {
      success
      message
      data {
        id
        fullname
        email
        instructorId
        contactNumber
        classStyle
        classType
        studentsCount
        profileImage
        isActive
      }
      totalCount
    }
  }
`;
export const GET_INSTRUCTOR_BY_ID = gql`
  query GetInstructor($id: ID!) {
    getInstructor(id: $id) {
      success
      message
      data {
        id
        fullname
        email
        contactNumber
        instructorType
        instructorId
        instagramId
        address
        classStyle
        classType
        studentsCount
        profileImage
        idProof
        gallery
        about
        isActive
        created_at
        updated_at
      }
    }
  }
`;

export const GET_ATTENDANCE_BY_CLASS = gql`
  query GetAttendanceByClass($classId: ID!, $date: String) {
    getAttendanceByClass(classId: $classId, date: $date) {
      success
      message
      data {
        id
        date
        students {
          studentId
          studentName
          studentCustomId
          isPresent
          sessionCategory
          passName
          passType
        }
        presentCount
        absentCount
        class {
          id
          classId
          className
          instructorName
        }
      }
    }
  }
`;

export const GET_STUDENT_ATTENDANCE_HISTORY = gql`
  query GetStudentAttendanceHistory($classId: ID!, $studentId: ID!) {
    getStudentAttendanceHistory(classId: $classId, studentId: $studentId) {
      success
      message
      data {
        studentId
        studentName
        totalSessions
        presentCount
        absentCount
        history {
          date
          className
          isPresent
        }
      }
    }
  }
`;

export const GET_STUDENT_OVERALL_ATTENDANCE_HISTORY = gql`
  query GetStudentOverallAttendanceHistory($studentId: ID!) {
    getStudentOverallAttendanceHistory(studentId: $studentId) {
      success
      message
      data {
        studentId
        studentName
        totalSessions
        presentCount
        absentCount
        history {
          date
          className
          isPresent
        }
      }
    }
  }
`;

export const GET_STUDENT_LIST = gql`
  query GetStudentList($page: Int, $perPage: Int, $search: String) {
    getStudentList(page: $page, perPage: $perPage, search: $search) {
      success
      message
      data {
        id
        studentId
        fullName
        email
        phoneNumber
        dateOfBirth
        assignType
        assignToClass
        isActive
        accountStatus
        deletionPending
        deletionScheduledFor
        created_at
      }
      totalCount
      page
      perPage
      totalPages
    }
  }
`;

export const GET_STUDENT_BY_ID = gql`
  query GetStudentById($id: ID!) {
    getStudentById(id: $id) {
      success
      message
      data {
        id
        studentId
        fullName
        email
        phoneNumber
        additionalPhoneNumber
        dateOfBirth
        address
        assignType
        assignToClass
        isActive
        uploadPhoto
        created_at
        updated_at
      }
    }
  }
`;

export const GET_ALL_UPCOMING_IDS = gql`
  query GetAllUpcomingIds(
    $search: String
    $instructorName: String
    $instructorId: String
    $classType: ClassType
    $day: String
    $today: String
  ) {
    getClassList(
      page: 1
      perPage: 10000
      timeline: UPCOMING
      search: $search
      instructorName: $instructorName
      instructorId: $instructorId
      classType: $classType
      day: $day
      today: $today
    ) {
      data {
        id
      }
      totalCount
    }
  }
`;

export const GET_CLASS_CANCELLATION_REQUESTS = gql`
  query GetClassCancellationRequests($status: CancellationStatus) {
    getClassCancellationRequests(status: $status) {
      success
      message
      data {
        id
        classId
        className
        instructorName
        reason
        sessionDate
        startTime
        endTime
        status
        requestedById
        requestedByName
        requestedByEmail
        requestedByRole
        reviewedById
        reviewedByName
        reviewedByRole
        reviewedAt
        created_at
        updated_at
      }
    }
  }
`;

export const GET_PASS_LIST = gql`
  query GetPassList($page: Int, $perPage: Int, $search: String, $styleType: String, $instructorName: String, $status: String) {
    getPassList(page: $page, perPage: $perPage, search: $search, styleType: $styleType, instructorName: $instructorName, status: $status) {
      success
      message
      data {
        id
        passId
        passType
        noOfClasses
        validityDays
        passName
        passCost
        status
        description
        instructors
        classIds
        classNames
        classId
        className
        classScheduledDates
        classEndTime
        styleType
        offers {
          id
          offerName
          offerCode
          discount
          validFrom
          validTill
          isActive
        }
        created_at
        updated_at
      }
      totalCount
      page
      perPage
      totalPages
    }
  }
`;

export const GET_PASS_BY_ID = gql`
  query GetPassById($id: ID!) {
    getPassById(id: $id) {
      success
      message
      data {
        id
        passId
        passType
        noOfClasses
        validityDays
        passName
        passCost
        status
        description
        instructors
        classIds
        classNames
        classId
        className
        classScheduledDates
        classEndTime
        styleType
        offers {
          id
          offerName
          offerCode
          discount
          validFrom
          validTill
          isActive
        }
        created_at
        updated_at
      }
    }
  }
`;

export const GET_UNIQUE_PASS_INSTRUCTORS = gql`
  query GetUniquePassInstructors {
    getUniquePassInstructors
  }
`;

export const GET_OFFER_LIST = gql`
  query GetOfferList($page: Int, $perPage: Int, $search: String) {
    getOfferList(page: $page, perPage: $perPage, search: $search) {
      success
      message
      totalCount
      page
      perPage
      totalPages
      data {
        id
        offerName
        offerCode
        validFrom
        validTill
        discount
        passType
        applicablePasses
        created_at
        updated_at
      }
    }
  }
`;

export const GET_OFFER_BY_ID = gql`
  query GetOfferById($id: ID!) {
    getOfferById(id: $id) {
      success
      message
      data {
        id
        offerName
        offerCode
        validFrom
        validTill
        discount
        passType
        applicablePasses
        created_at
        updated_at
      }
    }
  }
`;

export const GET_EVENT_LIST = gql`
  query GetEventList(
    $page: Int
    $perPage: Int
    $search: String
    $startDate: String
    $endDate: String
    $instructorId: String
  ) {
    getEventList(
      page: $page
      perPage: $perPage
      search: $search
      startDate: $startDate
      endDate: $endDate
      instructorId: $instructorId
    ) {
      success
      message
      totalCount
      page
      perPage
      totalPages
      data {
        id
        eventName
        location
        startDate
        endDate
        duration
        time
        standardPrice
        thumbnail
        instructors
        instructorNames
        description
        isOfferActive
        promoCode
        offerStartDate
        offerEndDate
        offerPrice
        offers {
          promoCode
          offerStartDate
          offerEndDate
          offerPrice
        }
        created_at
        updated_at
      }
    }
  }
`;

export const GET_EVENT_BY_ID = gql`
  query GetEventById($id: ID!) {
    getEventById(id: $id) {
      success
      message
      data {
        id
        eventName
        location
        startDate
        endDate
        duration
        time
        standardPrice
        gallery
        thumbnail
        instructors
        instructorNames
        description
        isOfferActive
        promoCode
        offerStartDate
        offerEndDate
        offerPrice
        offers {
          promoCode
          offerStartDate
          offerEndDate
          offerPrice
        }
        created_at
        updated_at
      }
    }
  }
`;

export const GET_INBOX = gql`
  query GetInbox {
    getInbox {
      success
      message
      data {
        id
        senderName
        title
        message
        isRead
        created_at
      }
    }
  }
`;

export const GET_SENT_NOTIFICATIONS = gql`
  query GetSentNotifications {
    getSentNotifications {
      success
      message
      data {
        id
        senderName
        title
        message
        targetAudience
        targetInstructorId
        targetClassId
        targetClassName
        targetUserId
        isRead
        created_at
      }
    }
  }
`;

export const GET_NOTIFICATION_OUTBOX = gql`
  query GetNotificationOutbox {
    getNotificationOutbox {
      success
      message
      data {
        id
        senderId
        senderName
        senderEmail
        senderRole
        title
        message
        targetAudience
        targetInstructorId
        targetClassId
        targetClassName
        targetUserId
        targetStudentName
        receiverEmail
        created_at
      }
    }
  }
`;

export const GET_EVENT_BOOKINGS = gql`
  query GetEventBookings($eventId: String!) {
    getEventBookings(eventId: $eventId) {
      success
      message
      data {
        id
        studentId
        studentName
        studentEmail
        bookedAt
        status
      }
      totalCount
    }
  }
`;

export const GET_STUDENT_EVENT_BOOKINGS = gql`
  query GetStudentEventBookings($studentId: String!) {
    getStudentEventBookings(studentId: $studentId) {
      success
      message
      data {
        id
        studentId
        studentName
        studentEmail
        eventId
        eventName
        bookedAt
        status
        amount
        currency
      }
      totalCount
    }
  }
`;

export const GET_MY_EVENT_BOOKINGS = gql`
  query GetMyEventBookings {
    getMyEventBookings {
      success
      message
      data {
        id
        eventId
        eventName
        bookedAt
        status
      }
      totalCount
    }
  }
`;

export const GET_MY_PURCHASED_PASSES = gql`
  query GetMyPurchasedPasses {
    getMyPurchasedPasses {
      success
      message
      data {
        id
        passDbId
        passName
        passType
        classId
        className
        classIds
        classNames
        bookedSessionDate
        bookedSessionDates
        bookedSessions {
          classId
          sessionDate
        }
        classScheduledDates
        classEndTime
        noOfClasses
        orderId
        amount
        currency
        paymentStatus
        invoiceAvailable
        purchasedAt
        expiryDate
        validityDays
        status
      }
      totalCount
    }
  }
`;

export const GET_STUDENT_PURCHASED_PASSES = gql`
  query GetStudentPurchasedPasses($studentId: String!) {
    getStudentPurchasedPasses(studentId: $studentId) {
      success
      message
      data {
        id
        passDbId
        passName
        passType
        classId
        className
        classIds
        classNames
        bookedSessionDate
        bookedSessionDates
        bookedSessions {
          classId
          sessionDate
        }
        classScheduledDates
        classEndTime
        noOfClasses
        orderId
        amount
        currency
        paymentStatus
        invoiceAvailable
        purchasedAt
        expiryDate
        validityDays
        status
      }
      totalCount
    }
  }
`;

export const GET_DASHBOARD_COUNTS = gql`
  query GetDashboardCounts {
    getStudentList(page: 1, perPage: 1) {
      totalCount
    }
    getInstructorList(page: 1, perPage: 1) {
      totalCount
    }
    getAdminList(page: 1, perPage: 1) {
      totalCount
    }
    getPassList(page: 1, perPage: 1) {
      totalCount
    }
    getOfferList(page: 1, perPage: 1) {
      totalCount
    }
    getEventList(page: 1, perPage: 1) {
      totalCount
    }
  }
`;

export const GET_INSTRUCTOR_ACTIVITIES = gql`
  query GetInstructorActivities {
    getInstructorActivities {
      name
      detail
      time
    }
  }
`;

export const GET_SUPERADMIN_DASHBOARD_DATA = gql`
  query GetSuperAdminDashboardData {
    getSuperAdminDashboardData {
      counts {
        students
        instructors
        admins
        classes
        passes
        offers
        events
        monthlyRevenue
      }
      instructorActivities {
        name
        detail
        time
      }
      recentOffers {
        id
        offerName
        offerCode
        discount
        validTill
      }
      upcomingEvents {
        id
        eventName
        location
        startDate
        time
      }
      recentStudents {
        id
        fullName
        assignToClass
        isActive
        created_at
      }
    }
  }
`;

export const GET_SUPERADMIN_REVENUE_INSIGHTS = gql`
  query GetSuperAdminRevenueInsights($startDate: String, $endDate: String) {
    getSuperAdminRevenueInsights(startDate: $startDate, endDate: $endDate) {
      passCount
      classesCount
      eventCount
      points {
        date
        month
        pass
        passCount
        classes
        classesCount
        event
        eventCount
      }
    }
  }
`;

export const GET_INSTRUCTOR_DASHBOARD_DATA = gql`
  query GetInstructorDashboardData($instructorId: String, $startDate: String, $endDate: String) {
    getInstructorDashboardData(instructorId: $instructorId, startDate: $startDate, endDate: $endDate) {
      instructor {
        id
        fullname
        email
        instructorId
      }
      classes {
        id
        className
        day
        scheduledDates
        startTime
        endTime
        classType
        instructorName
        instructorId
        isCancelled
        cancelledDates
      }
      updates {
        updates {
          id
          type
          title
          message
          time
          isNew
        }
        newCount
      }
    }
  }
`;

export const GET_RECENT_BILLING = gql`
  query GetRecentBilling($limit: Int) {
    getRecentBilling(limit: $limit) {
      success
      message
      data {
        id
        title
        subtitle
        amount
        currency
        date
        itemType
        status
        itemCount
        breakdown
      }
    }
  }
`;

export const GET_BILLING_SUMMARY = gql`
  query GetBillingSummary {
    getBillingSummary {
      success
      message
      data {
        stats {
          totalRevenue
          passPurchasesCount
          offersUsedCount
          eventTicketsSold
          revenueGrowth
          passPurchasesGrowth
          offersUsedGrowth
          eventTicketsSoldGrowth
        }
        recentTransactions {
          id
          orderId
          title
          subtitle
          amount
          currency
          date
          itemType
          status
          refundStatus
          refundAmount
          refundedAt
          refundFailureReason
          canRefund
          canSyncRefund
          itemCount
          breakdown
        }
        recentPassPurchases {
          name
          detail
          time
        }
        offerUsage {
          title
          subtitle
          uses
        }
        eventTicketSales {
          name
          date
          sold
          capacity
          revenue
        }
      }
    }
  }
`;

export const GET_PASS_PURCHASES = gql`
  query GetPassPurchases($passId: String!) {
    getPassPurchases(passId: $passId) {
      success
      message
      data {
        id
        studentId
        studentName
        passDbId
        passName
        passType
        classId
        className
        classIds
        classNames
        purchasedAt
        expiryDate
        validityDays
        status
      }
      totalCount
    }
  }
`;

export const GET_ATTENDANCE_REPORT = gql`
  query GetAttendanceReport($startDate: String, $endDate: String, $classId: String, $instructorId: String) {
    getAttendanceReport(startDate: $startDate, endDate: $endDate, classId: $classId, instructorId: $instructorId) {
      classId
      className
      instructorId
      instructorName
      date
      totalStudents
      presentCount
      attendancePercentage
      status
    }
  }
`;

export const GET_CLASS_PERFORMANCE_REPORT = gql`
  query GetClassPerformanceReport {
    getClassPerformanceReport {
      classId
      className
      instructorId
      instructorName
      totalCapacity
      bookedCount
      availableSlots
      fillRatePercentage
      status
    }
  }
`;

export const GET_INSTRUCTOR_ENGAGEMENT_REPORT = gql`
  query GetInstructorEngagementReport {
    getInstructorEngagementReport {
      instructorId
      instructorName
      totalClasses
      cancelledClassesCount
      avgAttendancePercentage
    }
  }
`;

export const GET_STUDENT_OVERVIEW_REPORT = gql`
  query GetStudentOverviewReport {
    getStudentOverviewReport {
      studentId
      customId
      studentName
      membershipType
      enrolledClasses
      attendancePercentage
      isActive
      lastActivity
    }
  }
`;

export const ACCOUNT_DELETION_IMPACT = gql`
  query AccountDeletionImpact($studentId: ID) {
    accountDeletionImpact(studentId: $studentId) {
      success
      message
      data {
        upcomingClasses
        activePasses
        eventBookings
        attendanceCount
        upcomingClassList {
          classId
          className
          startTime
          endTime
          dates
        }
      }
    }
  }
`;

export const STUDENT_AUDIT_HISTORY = gql`
  query StudentAuditHistory($studentId: ID!) {
    studentAuditHistory(studentId: $studentId) {
      success
      message
      data {
        id
        action
        oldStatus
        newStatus
        justification
        actorRole
        source
        created_at
      }
    }
  }
`;
